import { FC, useEffect, useRef, useState } from 'react';
import { Box, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { Form, Formik } from 'formik';
import { SubmitButton } from '../common/form/SubmitButton';
import { HtmlEditor } from '../common/html/HtmlEditor';
import { useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { QcsButton } from '../common/basic/QcsButton';
import { LanguageSelectWithoutFormik } from '../common/form/LanguageSelectWithoutFormik';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { QcsDialog } from '../common/basic/QcsDialog';
import { WorkplaceHandoverSectionGroupDto } from '@api/models/settings/WorkplaceHandoverSectionGroupDto';
import { companySettingsService } from '@api/services/services';

type Props = {
  /** if undefined, add mode */
  attachment?: WorkplaceHandoverSectionGroupDto;
  onClose: () => void;
  companyId: string;
};

export const AttachmentForm: FC<Props> = ({ attachment, onClose, companyId }) => {
  const { t } = useTranslation();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const [initialValues, setInitialValues] = useState<WorkplaceHandoverSectionGroupDto | null>(null);
  const [language, setLanguage] = useState<string>();

  const getHtmlRef = useRef<(() => string) | null>(null);
  const reloadHtmlRef = useRef<(() => void) | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const load = () => {
      const defaultLang = i18n.resolvedLanguage;

      setLanguage(defaultLang);

      if (!attachment) {
        const newSection: WorkplaceHandoverSectionGroupDto = {
          id: '',
          order: 0,
          sections:
            companyCustomization?.applicationLanguages?.map((lang) => ({
              id: '',
              title: '',
              content: '',
              language: lang,
            })) || [],
        };
        setInitialValues(newSection);
      } else {
        setInitialValues(attachment);
      }
    };

    load();
  }, [attachment, companyCustomization]);

  const updateSectionsWithNewContent = (
    sections: WorkplaceHandoverSectionGroupDto[],
    target: WorkplaceHandoverSectionGroupDto,
    newData: WorkplaceHandoverSectionGroupDto,
  ): WorkplaceHandoverSectionGroupDto[] =>
    sections.map((section) =>
      section.id === target.id
        ? {
            ...section,
            sections: section.sections.map((s) => {
              const update = newData.sections.find((s1) => s1.language === s.language);
              return update ? { ...s, ...update } : s;
            }),
          }
        : section,
    );

  const onSubmit = async (values: WorkplaceHandoverSectionGroupDto) => {
    const currentContent = getHtmlRef.current?.() || '';
    const updatedSections = values.sections.map((s) => (s.language === language ? { ...s, content: currentContent } : s));

    if (!values.sections.find((s) => s.language === i18n.resolvedLanguage)?.title && language !== i18n.resolvedLanguage) {
      setLanguage(i18n.resolvedLanguage);
      setTimeout(() => {
        (formRef.current?.querySelector('button[type="submit"]') as HTMLButtonElement | null)?.click();
      }, 100);
      return;
    }

    const allSections = (await companySettingsService.getWorkplaceHandoverSections()).data;
    if (!allSections) return;

    let updatedData: WorkplaceHandoverSectionGroupDto[];
    if (!values.id) {
      await companySettingsService.addWorkplaceHandoverSection();
      const newSections = (await companySettingsService.getWorkplaceHandoverSections()).data;

      const newSection = newSections.find((section) => !allSections.some((s) => s.id === section.id));
      if (!newSection) return;
      updatedData = [
        ...allSections,
        {
          ...newSection,
          sections: newSection.sections.map((s, i) => {
            const content = updatedSections.find((u) => u.language === s.language);
            return content ? { ...s, ...content, id: s.id, order: i } : s;
          }),
        },
      ];
    } else {
      updatedData = updateSectionsWithNewContent(allSections, values, {
        ...values,
        sections: updatedSections,
      });
    }
    await companySettingsService.updateWorkplaceHandoverSections(
      updatedData.map((s, i) => ({
        ...s,
        order: i,
      })),
    );
    onClose();
  };

  if (!initialValues || !language) return null;

  return (
    <QcsDialog open onClose={onClose}>
      <DialogTitle>{attachment ? t('handoverSettings.editSection') : t('handoverSettings.addSection')}</DialogTitle>

      <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmit}>
        {({ values, setFieldValue }) => {
          const currentSection = values.sections.find((s) => s.language === language);

          return (
            <Form ref={formRef}>
              <DialogContent>
                <Box mb={3}>
                  <LanguageSelectWithoutFormik
                    fullWidth
                    languages={SUPPORTED_LANGUAGES}
                    allowedLanguages={companyCustomization?.applicationLanguages}
                    value={language}
                    label={t('settings.companyPages.language')}
                    onChange={(e) => {
                      const currentHtml = getHtmlRef.current?.();
                      const updated = values.sections.map((s) => (s.language === language ? { ...s, content: currentHtml || '' } : s));
                      setFieldValue('sections', updated);
                      setLanguage(e.target.value);
                      requestAnimationFrame(() => reloadHtmlRef.current?.());
                    }}
                  />
                </Box>

                {currentSection && (
                  <Box mb={4}>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          label={t('handoverSettings.sectionTitle')}
                          required={language === i18n.resolvedLanguage}
                          value={currentSection.title}
                          onChange={(e) => {
                            const updated = [...values.sections];
                            const index = updated.findIndex((s) => s.language === language);
                            if (index !== -1) {
                              updated[index].title = e.target.value;
                              setFieldValue('sections', updated);
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={12}>
                        <HtmlEditor html={currentSection.content} getHtmlRef={getHtmlRef} reloadHtmlRef={reloadHtmlRef} allowSelectRisks={false} />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </DialogContent>

              <DialogActions>
                <QcsButton variant="text" onClick={onClose}>
                  {t('common.abort')}
                </QcsButton>
                <SubmitButton text={t('handoverSettings.addSection')} />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </QcsDialog>
  );
};

export default AttachmentForm;
